import { all, takeEvery, put, call, select } from 'redux-saga/effects';

import { firebaseFirestore } from 'services/firebase';
import firebase from 'firebase/app';

import { notification } from 'antd';

import { intl } from 'utils/intl';

import { setWinnerState, fetchWinnerGiftsStart } from './actions';

import WinnersActionTypes from './types';

import { getGiftsWonWithImageUrls, getWinnersWithCities, setClaimOnGift } from './utils';

function generateQueryToFetchUsers(lastGiftWonCampaign, queryRef) {
  const isFetchingOnCampaign = lastGiftWonCampaign !== null;
  return isFetchingOnCampaign
    ? queryRef.where('lastGiftWonCampaign', '==', lastGiftWonCampaign)
    : queryRef;
}

function* fetchWinnersAsync({ payload }) {
  const { pageSize, lastGiftWonCampaign } = yield select(state => state.winner);

  yield put(
    setWinnerState({ isFetchingWinners: payload === '', isSearchingWinners: payload !== '' }),
  );

  try {
    const searchQueryRef = generateQueryToFetchUsers(
      lastGiftWonCampaign,
      firebaseFirestore
        .collection('userDetails')
        .where('lastGiftWonDate', '!=', null)
        .where('keywords', 'array-contains', payload.toLowerCase())
        .orderBy('lastGiftWonDate', 'desc')
        .limit(pageSize),
    );
    const searchQuerySnapshot = yield searchQueryRef.get();
    const winners = yield getWinnersWithCities(searchQuerySnapshot);

    yield put(
      setWinnerState({
        winners,
        isFetchingWinners: false,
        isSearchingWinners: false,
        hasNextPage: !(winners.length < pageSize),
      }),
    );
  } catch (error) {
    notification.error({
      message: intl.formatMessage({ id: 'winners.notification.error.title' }),
      description: intl.formatMessage({
        id: 'winners.notification.fetch.error',
      }),
    });
    yield put(setWinnerState({ isFetchingWinners: false, hasNextPage: false }));
  }
}

function* fetchWinnerGiftsAsync({ payload }) {
  yield put(setWinnerState({ isFetchingWinnerGifts: true }));

  try {
    const winnerDoc = yield firebaseFirestore.collection('userDetails').doc(payload);
    const snapshot = yield winnerDoc.get();

    if (snapshot.empty) {
      yield put(
        setWinnerState({
          isFetchingWinnerGifts: false,
        }),
      );
    } else {
      const giftsWon = yield call(getGiftsWonWithImageUrls, snapshot);

      yield put(
        setWinnerState({
          isFetchingWinnerGifts: false,
          winnerGifts: giftsWon,
        }),
      );
    }
  } catch (error) {
    notification.error({
      message: intl.formatMessage({ id: 'winners.notification.error.title' }),
      description: intl.formatMessage({ id: 'winners.notification.gifts.fetch.error' }),
    });
    yield put(setWinnerState({ isFetchingWinnerGifts: false }));
  }
}

export function* nextPage() {
  const { winners, currentPage, pageSize, searchQuery, lastGiftWonCampaign } = yield select(
    state => state.winner,
  );

  const lastVisible = winners[winners.length - 1];

  yield put(setWinnerState({ isFetchingWinners: true }));

  try {
    const lastVisibleDoc = yield firebaseFirestore
      .collection('userDetails')
      .doc(lastVisible.id)
      .get();

    const query = generateQueryToFetchUsers(
      lastGiftWonCampaign,
      firebaseFirestore
        .collection('userDetails')
        .where('lastGiftWonDate', '!=', null)
        .where('keywords', 'array-contains', searchQuery.toLowerCase())
        .orderBy('lastGiftWonDate', 'desc')
        .startAfter(lastVisibleDoc)
        .limit(pageSize),
    );

    const snapshot = yield query.get();

    const winnersData = yield getWinnersWithCities(snapshot);

    yield put(
      setWinnerState({
        winners: winnersData.length > 0 ? winnersData : winners,
        hasNextPage: winnersData.length > 0,
        currentPage: winnersData.length > 0 ? currentPage + 1 : currentPage,
        isFetchingWinners: false,
      }),
    );

    if (winnersData.length === 0) {
      yield notification.warn({
        message: intl.formatMessage({ id: 'winners.notification.pagination.noData.title' }),
        description: intl.formatMessage({
          id: 'winners.notification.pagination.noData.description',
        }),
      });
    }
  } catch (error) {
    notification.error({
      message: intl.formatMessage({ id: 'winners.notification.error.title' }),
      description: intl.formatMessage({
        id: 'winners.notification.pagination.error',
      }),
    });
    yield put(setWinnerState({ isFetchingWinners: false, hasNextPage: false }));
  }
}

export function* prevPage() {
  const { winners, currentPage, pageSize, searchQuery, lastGiftWonCampaign } = yield select(
    state => state.winner,
  );

  const firstVisible = winners[0];

  yield put(setWinnerState({ isFetchingWinners: true }));

  try {
    const firstVisibleDoc = yield firebaseFirestore
      .collection('userDetails')
      .doc(firstVisible.id)
      .get();

    const query = generateQueryToFetchUsers(
      lastGiftWonCampaign,
      firebaseFirestore
        .collection('userDetails')
        .where('lastGiftWonDate', '!=', null)
        .where('keywords', 'array-contains', searchQuery.toLowerCase())
        .orderBy('lastGiftWonDate', 'desc')
        .endBefore(firstVisibleDoc)
        .limitToLast(pageSize),
    );

    const snapshot = yield query.get();

    const winnersData = yield getWinnersWithCities(snapshot);

    yield put(
      setWinnerState({
        winners: winnersData.length > 0 ? winnersData : winners,
        currentPage: winnersData.length > 0 ? currentPage - 1 : currentPage,
        hasNextPage: true,
        isFetchingWinners: false,
      }),
    );
  } catch (error) {
    notification.error({
      message: intl.formatMessage({ id: 'winners.notification.error.title' }),
      description: intl.formatMessage({
        id: 'winners.notification.pagination.error',
      }),
    });
    yield put(setWinnerState({ isFetchingWinners: false }));
  }
}

export function* reloadPage() {
  const { winners, pageSize, searchQuery, lastGiftWonCampaign } = yield select(
    state => state.winner,
  );

  const firstVisible = winners[0];

  yield put(setWinnerState({ loading: true }));

  try {
    const firstVisibleDoc = yield firebaseFirestore
      .collection('userDetails')
      .doc(firstVisible.id)
      .get();

    const query = generateQueryToFetchUsers(
      lastGiftWonCampaign,
      firebaseFirestore
        .collection('userDetails')
        .where('lastGiftWonDate', '!=', null)
        .where('keywords', 'array-contains', searchQuery.toLowerCase())
        .orderBy('lastGiftWonDate', 'desc')
        .startAt(firstVisibleDoc)
        .limit(pageSize),
    );

    const snapshot = yield query.get();

    const winnersData = yield getWinnersWithCities(snapshot);

    yield put(
      setWinnerState({
        winners: winnersData.length > 0 ? winnersData : winners,
        isFetchingWinners: false,
      }),
    );
  } catch (error) {
    notification.error({
      message: intl.formatMessage({ id: 'winners.notification.error.title' }),
      description: intl.formatMessage({
        id: 'winners.notification.refetch.error',
      }),
    });
    yield put(setWinnerState({ isFetchingWinners: false }));
  }
}

function* claimWinnerGiftAsync({ payload }) {
  yield put(setWinnerState({ isClaimingGift: true }));
  const { rowIndex, winnerId, isClaimed } = payload;
  try {
    const winnerDoc = yield firebaseFirestore.collection('userDetails').doc(winnerId);
    const snapshot = yield winnerDoc.get();
    const giftsWon = yield call(setClaimOnGift, snapshot, isClaimed, rowIndex);

    yield winnerDoc.set({ giftsWon }, { merge: true });

    yield put(
      setWinnerState({
        isClaimingGift: false,
        hasClaimedGift: true,
      }),
    );
    yield put(fetchWinnerGiftsStart(winnerId));
  } catch (error) {
    notification.error({
      message: intl.formatMessage({ id: 'winners.notification.error.title' }),
      description: intl.formatMessage({ id: 'winners.notification.gifts.claim.error' }),
    });
    yield put(setWinnerState({ isClaimingGift: false, hasClaimedGift: true }));
  }
}

function* searchCouponCode({ payload }) {
  yield put(setWinnerState({ isSearchingCouponCode: true }));

  try {
    const ref = yield firebaseFirestore.collection('couponCodes').doc(payload || '');
    const snapshot = yield ref.get();

    if (!snapshot.data()) {
      notification.error({
        message: intl.formatMessage({ id: 'winners.notification.searchCoupon.noData.title' }),
        description: intl.formatMessage({
          id: 'winners.notification.searchCoupon.noData.description',
        }),
      });
    }

    yield put(
      setWinnerState({
        isSearchingCouponCode: false,
        couponCode: snapshot.data() || null,
      }),
    );
  } catch (error) {
    notification.error({
      message: intl.formatMessage({ id: 'winners.notification.error.title' }),
      description: intl.formatMessage({ id: 'winners.notification.searchCoupon.error' }),
    });
    yield put(setWinnerState({ isSearchingCouponCode: false }));
  }
}

function* updateCouponCode({ payload }) {
  yield put(setWinnerState({ isUpdatingCouponCode: true }));

  try {
    const ref = yield firebaseFirestore.collection('couponCodes').doc(payload || '');
    const snapshot = yield ref.get();

    if (!snapshot.data()) {
      throw new Error();
    } else {
      const updatedTimestamp = firebase.firestore.Timestamp.fromDate(new Date());

      yield ref.set(
        { used: false, dateUsed: updatedTimestamp, updatedAt: updatedTimestamp },
        { merge: true },
      );

      const updated = yield ref.get();

      yield put(
        setWinnerState({
          isUpdatingCouponCode: false,
          couponCode: updated.data() || null,
        }),
      );
    }
  } catch (error) {
    notification.error({
      message: intl.formatMessage({ id: 'winners.notification.error.title' }),
      description: intl.formatMessage({ id: 'winners.notification.updateCoupon.error' }),
    });
    yield put(setWinnerState({ isUpdatingCouponCode: false }));
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(WinnersActionTypes.FETCH_WINNERS_START, fetchWinnersAsync),
    takeEvery(WinnersActionTypes.FETCH_WINNER_GIFTS_START, fetchWinnerGiftsAsync),
    takeEvery(WinnersActionTypes.CLAIM_WINNER_GIFT_START, claimWinnerGiftAsync),
    takeEvery(WinnersActionTypes.NEXT_PAGE, nextPage),
    takeEvery(WinnersActionTypes.PREV_PAGE, prevPage),
    takeEvery(WinnersActionTypes.RELOAD_PAGE, reloadPage),
    takeEvery(WinnersActionTypes.SEARCH_COUPON_CODE, searchCouponCode),
    takeEvery(WinnersActionTypes.UPDATE_COUPON_CODE, updateCouponCode),
  ]);
}
